"use client"

import { useState, useEffect, useRef } from "react"
import { Box, Text, Loader, Table, Group, Stack, Button } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { getOptionChain, type OptionChainRow } from "@/actions/options"

type Props = {
  creditType: string
  vintage: number
  projectRefId: string
}

/** Trading terminal's "Options" tab — Sean's requirement: an option chain next to the forward curve, filtered to the instrument on screen. */
export function OptionChainPanel({ creditType, vintage, projectRefId }: Props) {
  const t = useTranslations("trading")
  const [rows, setRows] = useState<OptionChainRow[]>([])
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const requestId = useRef(0)

  useEffect(() => {
    // Rapid showAll toggling can let an in-flight "show all" response land after a newer
    // "this market" request — only the latest request's result may commit to state.
    const thisRequestId = ++requestId.current
    setLoading(true)
    getOptionChain(showAll ? undefined : { creditType, vintage, projectRefId }).then((r) => {
      if (requestId.current !== thisRequestId) return
      setRows(r)
      setLoading(false)
    })
  }, [creditType, vintage, projectRefId, showAll])

  return (
    <Stack gap="md" p={8}>
      <Group justify="space-between">
        <Text fw={600} size="sm">{t("optionChainTab")}</Text>
        <Button variant="subtle" size="xs" onClick={() => setShowAll((v) => !v)}>
          {showAll ? t("optionChainShowThisMarket") : t("optionChainShowAll")}
        </Button>
      </Group>

      {loading ? (
        <Group justify="center"><Loader size="sm" /></Group>
      ) : rows.length === 0 ? (
        <Text c="dimmed" size="sm">{t("optionChainEmpty")}</Text>
      ) : (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("optionChainProject")}</Table.Th>
              <Table.Th>{t("optionChainVintage")}</Table.Th>
              <Table.Th ta="right">{t("optionChainStrike")}</Table.Th>
              <Table.Th ta="right">{t("optionChainMaxDuration")}</Table.Th>
              <Table.Th ta="right">{t("optionChainPremium")}</Table.Th>
              <Table.Th ta="right">{t("optionChainBlocksAvailable")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.refId}>
                <Table.Td>
                  <Box>
                    <Text size="sm">{row.projectName}</Text>
                    <Text size="xs" c="dimmed">{row.creditType}</Text>
                  </Box>
                </Table.Td>
                <Table.Td>{row.vintage}</Table.Td>
                <Table.Td ta="right">€{row.strikePrice.toFixed(2)}</Table.Td>
                <Table.Td ta="right">{row.expiryMonths}mo</Table.Td>
                <Table.Td ta="right">€{row.premiumPerTonne.toFixed(2)}/t</Table.Td>
                <Table.Td ta="right">{row.remainingTonnes}t</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}
